import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Camera, MapPin, Star, Calendar, MessageCircle, Check, ArrowLeft, BadgeCheck, LogOut } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { auth, photographers as photographersApi } from '../lib/api';
import { formatINR, formatDate, isAuthenticated, dashboardPathForRole, getStoredUser, clearSession } from '../lib/auth';

const PLACEHOLDER_COVER = 'https://images.unsplash.com/photo-1606800052052-a08af7148866?w=1200&h=400&fit=crop';
const PLACEHOLDER_AVATAR = 'https://images.unsplash.com/photo-1519741497674-611481863552?w=300&h=300&fit=crop';

const PhotographerProfile = () => {
  const { id } = useParams();
  const [photographer, setPhotographer] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  const user = getStoredUser();

  const handleLogout = async () => {
    try {
      await auth.logout();
    } catch {
      // ignore - we're clearing the local session regardless
    }
    clearSession();
    window.location.href = '/auth';
  };

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    photographersApi
      .get(id)
      .then(({ data }) => {
        if (!cancelled) setPhotographer(data);
      })
      .catch(() => {
        if (!cancelled) toast.error('Could not load this photographer.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  }

  if (!photographer) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <p className="text-muted-foreground">This photographer could not be found.</p>
        <Link to="/photographers"><Button variant="outline">Back to photographers</Button></Link>
      </div>
    );
  }

  const services = photographer.services || [];
  const reviews = photographer.reviews || [];
  const bookableOptions = services.flatMap((service) =>
    service.packages?.length
      ? service.packages.map((pkg) => ({
          key: `${service.id}-${pkg.id}`,
          name: `${service.title} — ${pkg.name}`,
          price: pkg.price,
          features: pkg.features || [],
          serviceId: service.id,
          packageId: pkg.id,
        }))
      : [
          {
            key: `${service.id}`,
            name: service.title,
            price: service.price,
            features: service.deliverables || [],
            serviceId: service.id,
            packageId: null,
          },
        ]
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-background sticky top-0 z-50">
        <div className="px-6 md:px-12 lg:px-24 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2" data-testid="logo-link">
              <Camera className="w-8 h-8" />
              <span className="font-playfair text-2xl font-bold">Pixnest</span>
            </Link>
            <div className="flex items-center space-x-4">
              {isAuthenticated() ? (
                <>
                  <Link to={dashboardPathForRole(user?.role)}>
                    <Button size="sm" data-testid="header-dashboard">Dashboard</Button>
                  </Link>
                  <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="header-logout">
                    <LogOut className="w-4 h-4 mr-2" />
                    Log Out
                  </Button>
                </>
              ) : (
                <Link to="/auth">
                  <Button variant="ghost" size="sm" data-testid="header-signin">Sign In</Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Back Button */}
      <div className="px-6 md:px-12 lg:px-24 py-6">
        <Link to="/photographers" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground" data-testid="back-button">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Photographers
        </Link>
      </div>

      {/* Cover Image */}
      <div className="relative h-80 mb-8">
        <img src={photographer.cover_photo_path || PLACEHOLDER_COVER} alt="Cover" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-background to-transparent" />
      </div>

      <div className="px-6 md:px-12 lg:px-24 pb-24">
        {/* Profile Header */}
        <div className="flex flex-col md:flex-row gap-8 mb-12 -mt-24 relative z-10">
          <img
            src={photographer.user?.avatar_path || PLACEHOLDER_AVATAR}
            alt={photographer.business_name}
            className="w-32 h-32 rounded-full object-cover border-4 border-background shadow-xl"
            data-testid="photographer-avatar"
          />
          <div className="flex-1">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div>
                <h1 className="font-playfair text-4xl font-bold mb-2 flex items-center gap-2" data-testid="photographer-name">
                  {photographer.business_name || photographer.user?.name}
                  {photographer.is_verified && <BadgeCheck className="w-6 h-6 text-primary" title="Verified photographer" />}
                </h1>
                <div className="flex flex-wrap items-center gap-4 mb-4">
                  {(photographer.specialties || []).map((s) => (
                    <Badge key={s} variant="secondary" className="capitalize" data-testid="photographer-specialty">{s}</Badge>
                  ))}
                  <div className="flex items-center text-muted-foreground">
                    <MapPin className="w-4 h-4 mr-1" />
                    <span data-testid="photographer-location">
                      {[photographer.city, photographer.state, photographer.country].filter(Boolean).join(', ') || 'Location not set'}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <Star className="w-4 h-4 fill-yellow-400 text-yellow-400 mr-1" />
                    <span className="font-semibold mr-1" data-testid="photographer-rating">{Number(photographer.avg_rating || 0).toFixed(1)}</span>
                    <span className="text-muted-foreground" data-testid="photographer-reviews">({photographer.review_count || 0} reviews)</span>
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="lg" variant="outline" onClick={() => toast.info('Direct messaging is coming soon.')} data-testid="message-button">
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Message
                </Button>
                <Link to={isAuthenticated() ? `/book/${photographer.id}` : '/auth'}>
                  <Button size="lg" data-testid="book-button">
                    <Calendar className="w-4 h-4 mr-2" />
                    Book Now
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Left Column - About & Services */}
          <div className="lg:col-span-2 space-y-12">
            {/* About */}
            <section data-testid="about-section">
              <h2 className="font-playfair text-3xl font-semibold mb-4" data-testid="about-title">About</h2>
              <p className="text-lg leading-relaxed text-muted-foreground" data-testid="about-text">
                {photographer.bio || 'This photographer has not added a bio yet.'}
              </p>
              {photographer.years_experience > 0 && (
                <p className="text-sm text-muted-foreground mt-2">{photographer.years_experience} years of experience</p>
              )}
            </section>

            {/* Services */}
            <section data-testid="portfolio-section">
              <h2 className="font-playfair text-3xl font-semibold mb-6" data-testid="portfolio-title">Services</h2>
              {services.length === 0 && <p className="text-muted-foreground">No services listed yet.</p>}
              <div className="space-y-4">
                {services.map((service) => (
                  <Card key={service.id} data-testid={`service-${service.id}`}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between gap-4 mb-2">
                        <h3 className="font-playfair text-xl font-semibold">{service.title}</h3>
                        <div className="font-playfair text-2xl font-bold whitespace-nowrap">{formatINR(service.price)}</div>
                      </div>
                      {service.category && <Badge variant="outline" className="capitalize mb-2">{service.category}</Badge>}
                      {service.description && <p className="text-muted-foreground mb-2">{service.description}</p>}
                      <div className="text-sm text-muted-foreground">
                        {service.duration_minutes ? `${Math.round(service.duration_minutes / 60)} hours` : null}
                        {service.delivery_days ? ` • Delivered within ${service.delivery_days} days` : null}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>

            {/* Reviews */}
            <section data-testid="reviews-section">
              <h2 className="font-playfair text-3xl font-semibold mb-6" data-testid="reviews-title">Reviews</h2>
              {reviews.length === 0 && <p className="text-muted-foreground">No reviews yet.</p>}
              <div className="space-y-6">
                {reviews.map((review, index) => (
                  <Card key={review.id || index} data-testid={`review-${index}`}>
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center font-semibold">
                          {(review.client?.name || '?').charAt(0)}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <div className="font-semibold" data-testid={`review-name-${index}`}>{review.client?.name || 'Anonymous'}</div>
                              <div className="text-sm text-muted-foreground" data-testid={`review-date-${index}`}>{formatDate(review.created_at)}</div>
                            </div>
                            <div className="flex">
                              {[...Array(review.rating)].map((_, i) => (
                                <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                              ))}
                            </div>
                          </div>
                          <p className="text-muted-foreground" data-testid={`review-text-${index}`}>{review.comment}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>
          </div>

          {/* Right Column - Bookable options */}
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <h2 className="font-playfair text-3xl font-semibold mb-6" data-testid="packages-title">Packages</h2>
              {bookableOptions.length === 0 && <p className="text-muted-foreground">This photographer has no bookable services yet.</p>}
              <div className="space-y-4">
                {bookableOptions.map((option, index) => (
                  <Card key={option.key} data-testid={`package-${index}`}>
                    <CardContent className="p-6">
                      <h3 className="font-playfair text-2xl font-semibold mb-2" data-testid={`package-name-${index}`}>{option.name}</h3>
                      <div className="font-playfair text-4xl font-bold mb-4" data-testid={`package-price-${index}`}>{formatINR(option.price)}</div>
                      <ul className="space-y-2 mb-6">
                        {option.features.map((feature, fIndex) => (
                          <li key={fIndex} className="flex items-start text-sm" data-testid={`package-feature-${index}-${fIndex}`}>
                            <Check className="w-4 h-4 mr-2 text-primary flex-shrink-0 mt-0.5" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                      <Link
                        to={
                          isAuthenticated()
                            ? `/book/${photographer.id}?service=${option.serviceId}${option.packageId ? `&package=${option.packageId}` : ''}`
                            : '/auth'
                        }
                      >
                        <Button className="w-full" data-testid={`package-book-button-${index}`}>Select Package</Button>
                      </Link>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhotographerProfile;
